import React, { useState, useEffect } from 'react';
import { FinanceService, ICreateFinanceRequest } from '../../services/finance.service';
import { Money, checkAgainstBalance, kopecksToDecimal, parseMoneyInput } from '../../utils/money';
import './addFinanceRecordModal.css';

interface AddFinanceRecordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  autoparkId: string;
  autoparkBalance: Money;
}

function AddFinanceRecordModal({
  isOpen,
  onClose,
  onConfirm,
  autoparkId,
  autoparkBalance,
}: AddFinanceRecordModalProps) {
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setAmount('');
      setError('');
      // Блокируем скролл при открытии модального окна
      document.body.style.overflow = 'hidden';
    } else {
      // Разблокируем скролл при закрытии
      document.body.style.overflow = 'unset';
    }

    // Очистка при размонтировании компонента
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Разрешаем только числа и одну точку/запятую
    if (/^\d*[.,]?\d*$/.test(value)) {
      setAmount(value);
      setError('');
    }
  };

  const handleConfirm = async () => {
    const kopecks = parseMoneyInput(amount);

    if (!amount.trim()) {
      setError('Введите сумму');
      return;
    }

    if (kopecks === null || kopecks <= 0) {
      setError('Введите корректную сумму');
      return;
    }

    const balanceCheck = checkAgainstBalance(kopecks, autoparkBalance);

    if (balanceCheck === 'unknown') {
      setError('Не удалось определить баланс автопарка');
      return;
    }

    if (balanceCheck === 'exceeds') {
      setError('Сумма не может быть больше баланса автопарка');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const requestData: ICreateFinanceRequest = {
        autoparkId: autoparkId,
        price: kopecksToDecimal(kopecks),
        status: 'notPaid',
      };

      await FinanceService.createBalance(requestData);

      onConfirm();
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleConfirm();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="add-finance-modal-modal-overlay" onClick={handleBackdropClick}>
      {/* Кнопка закрытия */}
      <button className="modal-close-btn" onClick={onClose}>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <g clip-path="url(#clip0_354_1355)">
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M11.9993 13.4141L17.6563 19.0711C17.8449 19.2533 18.0975 19.3541 18.3597 19.3518C18.6219 19.3495 18.8727 19.2444 19.0582 19.059C19.2436 18.8736 19.3487 18.6227 19.351 18.3606C19.3533 18.0984 19.2525 17.8458 19.0703 17.6571L13.4133 12.0001L19.0703 6.34315C19.2525 6.15455 19.3533 5.90194 19.351 5.63975C19.3487 5.37755 19.2436 5.12674 19.0582 4.94133C18.8727 4.75592 18.6219 4.65075 18.3597 4.64848C18.0975 4.6462 17.8449 4.74699 17.6563 4.92915L11.9993 10.5861L6.34234 4.92915C6.15288 4.75149 5.90175 4.65451 5.64207 4.65873C5.38238 4.66295 5.13453 4.76803 4.95094 4.95174C4.76736 5.13546 4.66245 5.38339 4.65842 5.64307C4.65439 5.90276 4.75155 6.15382 4.92933 6.34315L10.5853 12.0001L4.92833 17.6571C4.83282 17.7494 4.75664 17.8597 4.70423 17.9817C4.65182 18.1037 4.62424 18.235 4.62308 18.3677C4.62193 18.5005 4.64723 18.6322 4.69751 18.7551C4.74779 18.878 4.82205 18.9897 4.91594 19.0835C5.00983 19.1774 5.12148 19.2517 5.24438 19.302C5.36728 19.3523 5.49896 19.3776 5.63174 19.3764C5.76452 19.3752 5.89574 19.3477 6.01774 19.2953C6.13974 19.2428 6.25009 19.1667 6.34234 19.0711L11.9993 13.4141Z"
              fill="#3563E9"
            />
          </g>
          <defs>
            <clipPath id="clip0_354_1355">
              <rect width="24" height="24" fill="white" />
            </clipPath>
          </defs>
        </svg>
      </button>
      <div className="add-finance-modal-wrapper">
        <div className="add-finance-modal">
          {/* Заголовок */}
          <h2 className="modal-title">Ввести сумму к выплате</h2>

          {/* Поле ввода */}
          <div className="input-container">
            <input
              type="text"
              value={amount}
              onChange={handleAmountChange}
              onKeyDown={handleKeyDown}
              placeholder="Введите сумму"
              className={`amount-input ${error ? 'error' : ''}`}
              autoFocus
            />
            {error && <div className="error-message">{error}</div>}
          </div>

          {/* Кнопки */}
          <div className="modal-actions">
            <button className="confirm-btn" onClick={handleConfirm} disabled={loading}>
              {loading ? 'Создание...' : 'Подтвердить'}
            </button>
            <button className="cancel-btn" onClick={onClose} disabled={loading}>
              Отменить
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddFinanceRecordModal;
